import { saveDraftImage } from "./draftImageStore";
import { canonicalGameToDisplayLabel } from "./canonicalGames";
import {
  getPostTradeDraftKeys,
  readPostTradeDraft,
  writePostTradeDraft,
} from "./postTradeDraft";

export interface PrefillCardIdentity {
  cardId?: string | null;
  cardName?: string | null;
  game?: string | null;
  [key: string]: unknown;
}

interface PreparePostTradeDraftInput {
  userId?: string | number | null;
  confirmedCard: PrefillCardIdentity | null | undefined;
  cardName?: string | null;
  sourceContext?: string | null;
  condition?: string | null;
  gradingCompany?: string | null;
  gradeNumber?: string | null;
  frontFile?: Blob | null;
  frontImageUrl?: string | null;
  backImageUrl?: string | null;
  imageFileName?: string | null;
  blockOnImageFailure?: boolean;
}

interface PreparePostTradeDraftResult {
  ok: boolean;
  warning?: string;
  error?: string;
}

function normalizeImageExtension(mimeType: string): string {
  const normalized = String(mimeType || "").trim().toLowerCase();
  if (normalized === "image/png") return "png";
  if (normalized === "image/jpeg" || normalized === "image/jpg") return "jpg";
  return "webp";
}

function sanitizeFileStem(value: string): string {
  return String(value || "trade-image")
    .trim()
    .replace(/[^a-z0-9-_]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64) || "trade-image";
}

async function resolveImageBlob(input: PreparePostTradeDraftInput): Promise<Blob> {
  if (input.frontFile) {
    return input.frontFile;
  }

  const imageUrl = String(input.frontImageUrl || "").trim();
  if (!imageUrl) {
    throw new Error("No trade image is available for this card.");
  }

  const response = await fetch(imageUrl);
  if (!response.ok) {
    throw new Error("Could not load this card image for trade draft.");
  }

  const blob = await response.blob();
  if (!blob || !blob.size) {
    throw new Error("Card image is empty and cannot be used for trade draft.");
  }

  return blob;
}

export async function preparePostTradeDraftFromCard(
  input: PreparePostTradeDraftInput,
): Promise<PreparePostTradeDraftResult> {
  const confirmedCard = input.confirmedCard || null;
  const cardId = String(confirmedCard?.cardId || "").trim();
  const game = String(confirmedCard?.game || "").trim();

  if (!cardId || !game) {
    return {
      ok: false,
      error: "This card is missing canonical identity details and cannot be posted to trade.",
    };
  }

  const { storageKey, imageKey } = getPostTradeDraftKeys(input.userId ?? null);
  const existingDraft = readPostTradeDraft(storageKey);
  const existingFormData =
    existingDraft?.formData && typeof existingDraft.formData === "object" ? existingDraft.formData : {};

  const nextCardName =
    String(input.cardName || confirmedCard?.cardName || existingFormData.cardName || "").trim() ||
    String(confirmedCard?.cardName || "").trim();
  if (!nextCardName) {
    return {
      ok: false,
      error: "This card is missing a name and cannot be used for trade draft.",
    };
  }

  const nextGame =
    canonicalGameToDisplayLabel(confirmedCard?.game) ||
    String(existingFormData.game || "Magic: The Gathering");

  const nextFormData = {
    ...existingFormData,
    sourceContext: String(input.sourceContext || "").trim().toLowerCase() || null,
    cardName: nextCardName,
    game: nextGame,
    condition: input.condition || String(existingFormData.condition || "Near Mint"),
    gradingCompany: input.gradingCompany || String(existingFormData.gradingCompany || "raw"),
    gradeNumber: input.gradeNumber || String(existingFormData.gradeNumber || ""),
    frontImageUrl: String(input.frontImageUrl || "").trim(),
    backImageUrl: String(input.backImageUrl || "").trim(),
    photoUrl: "",
  };

  try {
    const blob = await resolveImageBlob(input);
    const mimeType = String(blob.type || "image/webp") || "image/webp";
    const extension = normalizeImageExtension(mimeType);
    const baseName = sanitizeFileStem(
      String(input.imageFileName || nextCardName || "trade-image").replace(/\.[a-z0-9]+$/i, ""),
    );

    await saveDraftImage(imageKey, blob, {
      name: `${baseName}.${extension}`,
      type: mimeType,
      lastModified: Date.now(),
    });

    writePostTradeDraft(storageKey, nextFormData, {
      ...confirmedCard,
      cardId,
      cardName: String(confirmedCard?.cardName || nextCardName),
      game,
    });

    return { ok: true };
  } catch (error: any) {
    if (input.blockOnImageFailure === false) {
      writePostTradeDraft(storageKey, nextFormData, {
        ...confirmedCard,
        cardId,
        cardName: String(confirmedCard?.cardName || nextCardName),
        game,
      });

      return {
        ok: true,
        warning: error?.message || "Card confirmed, but the trade image could not be prepared.",
      };
    }

    return {
      ok: false,
      error: error?.message || "Failed to prepare trade draft image.",
    };
  }
}
